import { motion } from "framer-motion";
import { Zap, Timer, Ticket } from "lucide-react";
import { ASSETS } from "./questions";
import { EntryCounter } from "./EntryCounter";

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { delay: 0.15 + i * 0.13, type: "spring", stiffness: 150, damping: 20 },
  }),
};

const MARQUEE_ITEMS = ["50 EXTRA ENTRIES", "UNDER 1 MINUTE", "GIANT EAGLE PLUSHIE", "LET'S GO EAGLES"];

export const IntroScreen = ({ onStart }) => (
  <div data-testid="intro-screen" className="flex-1 flex flex-col px-6 pt-6 pb-0">
    <div className="font-display text-4xl sm:text-5xl font-black uppercase leading-[1.02] tracking-tight text-slate-900">
      {["Win the Giant", "Ashley Falls", "Eagle!"].map((line, i) => (
        <div key={line} className="overflow-hidden pb-1">
          <motion.div custom={i} variants={lineReveal} initial="hidden" animate="show">
            {i === 1 ? (
              <span className="notranslate">{line}</span>
            ) : i === 2 ? (
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-500 to-yellow-400">
                {line}
              </span>
            ) : (
              line
            )}
          </motion.div>
        </div>
      ))}
    </div>

    <motion.div
      className="relative mt-6 self-center"
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.5, type: "spring", stiffness: 120, damping: 16 }}
    >
      <div className="absolute inset-0 -z-10 rounded-full bg-[radial-gradient(circle,rgba(6,182,212,0.18),rgba(236,72,153,0.12)_55%,transparent_75%)] blur-xl scale-125" />
      <motion.img
        src={ASSETS.plushie}
        alt="Giant Ashley Falls Eagle Plushie"
        className="h-56 sm:h-64 w-auto drop-shadow-[0_16px_28px_rgba(15,23,42,0.18)]"
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.span
        className="absolute -right-3 top-2 rounded-full border-2 border-yellow-400 bg-white px-3 py-1.5 font-display text-xs font-black text-slate-900 shadow-[0_0_16px_rgba(234,179,8,0.5)]"
        initial={{ opacity: 0, scale: 0, rotate: -12 }}
        animate={{ opacity: 1, scale: 1, rotate: 8 }}
        transition={{ delay: 1, type: "spring", stiffness: 260, damping: 14 }}
      >
        +50 ENTRIES
      </motion.span>
    </motion.div>

    <EntryCounter />

    <motion.p
      className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7 }}
    >
      Complete this quick survey to instantly earn{" "}
      <span className="font-bold text-pink-600">50 EXTRA ENTRIES</span> into the plushie drawing. It takes
      less than 1 minute (the average is 30 seconds!). Let's go!
    </motion.p>

    <motion.div
      className="mt-5 flex flex-wrap gap-2.5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.85 }}
    >
      <span className="flex items-center gap-1.5 rounded-full border border-cyan-300 bg-white px-3 py-1.5 text-xs font-bold text-cyan-700 shadow-[0_0_10px_rgba(6,182,212,0.2)]">
        <Ticket size={13} /> 50 extra entries
      </span>
      <span className="flex items-center gap-1.5 rounded-full border border-pink-300 bg-white px-3 py-1.5 text-xs font-bold text-pink-700 shadow-[0_0_10px_rgba(236,72,153,0.2)]">
        <Timer size={13} /> ~30 seconds
      </span>
      <span className="flex items-center gap-1.5 rounded-full border border-yellow-300 bg-white px-3 py-1.5 text-xs font-bold text-yellow-700 shadow-[0_0_10px_rgba(234,179,8,0.2)]">
        <Zap size={13} /> 9 quick taps
      </span>
    </motion.div>

    <motion.button
      data-testid="start-survey-button"
      onClick={onStart}
      className="neon-pulse mt-7 w-full rounded-2xl bg-gradient-to-r from-pink-500 via-fuchsia-500 to-cyan-500 px-6 py-5 font-display text-lg font-black uppercase tracking-wide text-white active:scale-[0.97] transition-transform"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1 }}
      whileTap={{ scale: 0.97 }}
    >
      Start Survey
    </motion.button>

    <div className="relative mt-8 -mx-6 overflow-hidden border-t border-b border-slate-100 bg-white py-3">
      <div className="marquee-track flex w-max items-center gap-6 whitespace-nowrap">
        {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
          <span key={i} className="flex items-center gap-6 font-display text-xs font-extrabold tracking-[0.25em] text-slate-300">
            {item}
            <span className={i % 3 === 0 ? "text-cyan-400" : i % 3 === 1 ? "text-pink-500" : "text-yellow-400"}>✦</span>
          </span>
        ))}
      </div>
    </div>
  </div>
);
